type Rgb = { r: number; g: number; b: number };

const TRANSPARENT_VALUES = new Set(['transparent', 'rgba(0,0,0,0)', 'rgba(0, 0, 0, 0)']);

const clampChannel = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${clampChannel(r).toString(16).padStart(2, '0')}${clampChannel(g).toString(16).padStart(2, '0')}${clampChannel(b)
    .toString(16)
    .padStart(2, '0')}`.toUpperCase();

const parseHex = (value: string): Rgb | null => {
  const hex = value.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
};

const parseRgb = (value: string): Rgb | null => {
  const match = value.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
};

const parseColor = (value?: string | null): Rgb | null => {
  if (!value) return null;
  const normalized = value.trim();
  if (!normalized || TRANSPARENT_VALUES.has(normalized.toLowerCase())) return null;
  if (normalized.startsWith('#')) return parseHex(normalized);
  if (normalized.startsWith('rgb')) return parseRgb(normalized);
  return null;
};

export const blendColor = (
  baseColor: string | undefined | null,
  mixColor: string,
  ratio: number,
  fallbackColor?: string,
): string => {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const mix = parseColor(mixColor);
  const fallback = parseColor(fallbackColor);
  const base = parseColor(baseColor) ?? fallback ?? mix;

  if (!base || !mix) {
    return (baseColor ?? fallbackColor ?? mixColor).trim();
  }

  return rgbToHex({
    r: base.r + (mix.r - base.r) * clampedRatio,
    g: base.g + (mix.g - base.g) * clampedRatio,
    b: base.b + (mix.b - base.b) * clampedRatio,
  });
};
